<script>
	import { onDestroy } from "svelte";
	import { navigate } from "svelte-routing";
	import CustomBreadcrumb from "../../../components/CustomBreadcrumb/CustomBreadcrumb.svelte";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import Tabs from "../../../components/Tabs/Tabs.svelte";
	import VehicleIssuesList from "../Manage/VehicleIssues/VehicleIssuesList.svelte";
	import CreateIssueForm from "../Manage/VehicleIssues/CreateIssueForm.svelte";
	import DiscardChangesModal from "../Manage/VehicleIssues/DiscardChangesModal.svelte";
	import VehicleWorkOrder from "../Manage/VehicleWorkOrders/VehicleWorkOrder.svelte";
	import VehicleActivityLog from "../Manage/VehicleActivityLog/VehicleActivityLog.svelte";
	import VehicleDetails from "../Manage/VehicleDetails/VehicleDetails.svelte";
	import WorkOrderDetail from "../WorkOrderDetail/WorkOrderDetail.svelte";
	import AddJob from "../WorkOrderDetail/AddJob.svelte";
	import {
		activeMaintenanceRepairTab,
		errorMessage,
		userRole,
	} from "src/store/store";
	import { usesWorkshopView } from "src/config/permissions";
	import { isMnrPhase2Enabled } from "src/config/flag-config";
	import { formatErrorMessage } from "src/utils/misc";
	import {
		getMaintenanceRepairVehicleById,
		getWorkOrderById,
	} from "src/services/maintenanceRepair";
	import { knownPlateNumber } from "src/services/maintenanceRepair/plateNumberCache";
	import {
		DEFAULT_TAB_VALUE,
		tabValueFromSlug,
		vehicleTabPath,
	} from "./tabRoutes";

	export let vehicleId;
	export let workOrderId = null;
	export let isCreatingIssue = false;
	export let editingIssueId = null;
	export let tab = undefined;

	$: isEditingIssue = !!editingIssueId;
	let editingIssueCode = "";
	$: (editingIssueId, (editingIssueCode = ""));

	// Workshop users don't have access to the vehicle issues list — that
	// route falls through to NotFound for them (see MaintenanceRepair.svelte).
	$: isWshop = usesWorkshopView($userRole);

	let workOrder = null;
	let isLoadingWorkOrder = false;

	// Single error banner for all tabs. Tabs report load errors via onError;
	// unlike the global errorMessage, client() doesn't clear it on success.
	let pageError = "";
	function reportPageError(message) {
		pageError = message || "";
	}
	$: (activeTabValue, vehicleId, reportPageError(""));

	// errorMessage is shared across pages.
	errorMessage.set("");
	onDestroy(() => errorMessage.set(""));

	let vehiclePlateNumber = "";
	let latestRequestId = 0;

	// Reactive on vehicleId (not onMount) so the header and breadcrumb follow a
	// vehicle change that doesn't remount the page, e.g. back/forward between
	// two vehicle URLs; only the latest response is applied.
	async function loadPlateNumber(id, wshop) {
		// The workshop route puts the plate number in this segment instead of an
		// id, and the endpoint 404s on one, so it has nothing to look up.
		if (wshop || !/^\d+$/.test(String(id ?? ""))) {
			vehiclePlateNumber = "";
			return;
		}
		// The vehicle list caches what it already fetched, so a click from there
		// needs no request at all.
		vehiclePlateNumber = knownPlateNumber(id);
		if (vehiclePlateNumber) return;

		const requestId = ++latestRequestId;
		try {
			const res = await getMaintenanceRepairVehicleById(id);
			if (requestId !== latestRequestId) return;
			vehiclePlateNumber = res.data?.plateNumber || "";
		} catch (error) {
			// The title just keeps the vehicleId fallback; each tab reports its
			// own load failures, so there's nothing more to show here.
			if (requestId === latestRequestId) console.error(error);
		}
	}

	$: loadPlateNumber(vehicleId, isWshop);

	// Seeded from the URL so a direct/bookmarked link still opens on AddJob,
	// but kept mutable (not remount-driven) since navigate() to the same
	// route pattern doesn't remount this component — only the query string
	// changes, so a click-driven toggle has to flip this directly.
	let isAddingJob =
		!!workOrderId &&
		new URLSearchParams(window.location.search).has("addJob");

	// The URL decides the tab: the work order detail route is always Work
	// Orders, everything else reads the :tab segment.
	const tabValueForRoute = (slug, forWorkOrderId, forWshop) =>
		forWorkOrderId || forWshop
			? 2
			: (tabValueFromSlug(slug) ?? DEFAULT_TAB_VALUE);

	const initialActiveTab = tabValueForRoute(tab, workOrderId, false);
	let activeTabValue = initialActiveTab;

	$: routeTabValue = tabValueForRoute(tab, workOrderId, isWshop);
	$: activeTabValue = routeTabValue;
	// Only a remembered "last used tab" for the bare /:vehicleId redirect.
	$: activeMaintenanceRepairTab.set(routeTabValue);

	let isCreateFormDirty = false;
	let isDiscardModalOpen = false;
	let pendingTabValue = null;

	// A tab click only changes activeTabValue locally (Tabs.svelte binds it),
	// so turn it into navigation to that tab's URL.
	function handleTabSelected(value) {
		if (value === tabValueForRoute(tab, workOrderId, isWshop)) return;
		if ((isCreatingIssue || isEditingIssue) && isCreateFormDirty) {
			pendingTabValue = value;
			isDiscardModalOpen = true;
			activeTabValue = routeTabValue;
			return;
		}
		navigate(vehicleTabPath(vehicleId, value));
	}
	$: handleTabSelected(activeTabValue);

	function keepEditing() {
		isDiscardModalOpen = false;
		pendingTabValue = null;
	}

	function discardAndLeave() {
		const value = pendingTabValue;
		isDiscardModalOpen = false;
		pendingTabValue = null;
		isCreateFormDirty = false;
		navigate(vehicleTabPath(vehicleId, value));
	}

	let latestWorkOrderRequestId = 0;

	async function loadWorkOrder(id) {
		if (!isMnrPhase2Enabled) return;
		const requestId = ++latestWorkOrderRequestId;
		isLoadingWorkOrder = true;
		try {
			const res = await getWorkOrderById(id);
			if (requestId !== latestWorkOrderRequestId) return;
			workOrder = res.data;
		} catch (error) {
			if (requestId !== latestWorkOrderRequestId) return;
			console.error(error);
			workOrder = null;
			reportPageError(
				formatErrorMessage(error) || "Couldn't load work order."
			);
		} finally {
			if (requestId === latestWorkOrderRequestId) {
				isLoadingWorkOrder = false;
			}
		}
	}

	$: if (workOrderId) {
		loadWorkOrder(workOrderId);
	} else {
		workOrder = null;
	}

	// Workshop users only get Work Orders — Issues/Activity log/Vehicle
	// details live under routes they don't have access to (see
	// MaintenanceRepair.svelte's allowlist).
	// AddJob only once a work order has actually loaded and can still take
	// jobs — a direct ?addJob link otherwise skips WorkOrderDetail's loading,
	// not-found and read-only (Completed/Cancelled) handling.
	$: canAddJob =
		!!workOrder &&
		!isLoadingWorkOrder &&
		!["Completed", "Cancelled"].includes(workOrder.status);

	$: workOrdersTabComponent = workOrderId
		? isAddingJob && canAddJob
			? AddJob
			: WorkOrderDetail
		: VehicleWorkOrder;

	$: items = !isMnrPhase2Enabled
		? [
				{
					label: "Issues",
					value: 1,
					component:
						isCreatingIssue || isEditingIssue
							? CreateIssueForm
							: VehicleIssuesList,
				},
			]
		: isWshop
			? [
					{
						label: "Work Orders",
						value: 2,
						component: workOrdersTabComponent,
					},
				]
			: [
					{
						label: "Issues",
						value: 1,
						component:
							isCreatingIssue || isEditingIssue
								? CreateIssueForm
								: VehicleIssuesList,
					},
					{
						label: "Work Orders",
						value: 2,
						component: workOrdersTabComponent,
					},
					{
						label: "Activity log",
						value: 3,
						component: VehicleActivityLog,
					},
					{
						label: "Vehicle details",
						value: 4,
						component: VehicleDetails,
					},
				];
</script>

<CustomBreadcrumb
	links={[
		{ label: "Maintenance & Repairs", to: "/maintenance-repair" },
		{
			label: vehiclePlateNumber || vehicleId,
			to: isWshop
				? undefined
				: workOrderId
					? vehicleTabPath(vehicleId, 2)
					: isCreatingIssue || isEditingIssue
						? vehicleTabPath(vehicleId, 1)
						: undefined,
		},
		...(workOrderId ? [{ label: workOrder?.code || workOrderId }] : []),
		...(isCreatingIssue ? [{ label: "Create issue" }] : []),
		...(isEditingIssue && editingIssueCode
			? [{ label: editingIssueCode, to: vehicleTabPath(vehicleId, 1) }]
			: []),
		...(isEditingIssue ? [{ label: "Edit issue" }] : []),
	]}
/>

<PageHeader title={vehiclePlateNumber || vehicleId} />

<ErrorMessageHandler message={pageError || $errorMessage} />

<div class="mt-4">
	<Tabs
		{items}
		hideTabBar={!isMnrPhase2Enabled}
		{vehicleId}
		onError={reportPageError}
		{workOrderId}
		{workOrder}
		onWorkOrderChange={(updatedWorkOrder) => (workOrder = updatedWorkOrder)}
		{isLoadingWorkOrder}
		onAddJob={() => (isAddingJob = true)}
		onCancelAddJob={() => (isAddingJob = false)}
		onDirtyChange={(isDirty) => (isCreateFormDirty = isDirty)}
		issueId={editingIssueId}
		onIssueLoaded={(issue) => (editingIssueCode = issue?.code || "")}
		activeTab={initialActiveTab}
		bind:activeTabValue
	/>
</div>

{#if isDiscardModalOpen}
	<DiscardChangesModal
		isOpen
		on:cancel={keepEditing}
		on:confirm={discardAndLeave}
	/>
{/if}
